/**
 * Lightweight sessionStorage cache for adventure data.
 * Allows instant rendering when navigating from Index → Play,
 * while the full load refreshes in the background.
 */

const CACHE_KEY = "adv-cache-v1";
const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

interface CachedAdventure {
  id: string;
  world_id: string;
  user_id: string;
  name: string;
  player_character: any;
  state: any;
  turn_mode: string;
  current_player_id: string | null;
  is_multiplayer: boolean;
  invite_code: string | null;
  cachedAt: number;
}

export function cacheAdventure(adv: {
  id: string;
  world_id: string;
  user_id?: string;
  name: string;
  player_character?: any;
  state?: any;
  turn_mode?: string;
  current_player_id?: string | null;
  is_multiplayer?: boolean;
  invite_code?: string | null;
}) {
  try {
    const entry: CachedAdventure = {
      id: adv.id,
      world_id: adv.world_id,
      user_id: adv.user_id || "",
      name: adv.name,
      player_character: adv.player_character || {},
      state: adv.state || {},
      turn_mode: adv.turn_mode || "free",
      current_player_id: adv.current_player_id ?? null,
      is_multiplayer: adv.is_multiplayer ?? false,
      invite_code: adv.invite_code ?? null,
      cachedAt: Date.now(),
    };
    sessionStorage.setItem(`${CACHE_KEY}-${adv.id}`, JSON.stringify(entry));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

export function getCachedAdventure(adventureId: string): Omit<CachedAdventure, "cachedAt"> | null {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY}-${adventureId}`);
    if (!raw) return null;
    const entry: CachedAdventure = JSON.parse(raw);
    if (Date.now() - entry.cachedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(`${CACHE_KEY}-${adventureId}`);
      return null;
    }
    const { cachedAt, ...rest } = entry;
    return rest;
  } catch {
    return null;
  }
}

export function clearAdventureCache(adventureId: string) {
  try {
    sessionStorage.removeItem(`${CACHE_KEY}-${adventureId}`);
  } catch {}
}
